import { useCallback, useEffect, useState } from 'react';
import {
  fetchLalamoveWebhookInfo,
  registerLalamoveWebhook,
} from '../../lib/deliveryArrangements';

/** Lalamove webhook register UI — used under Integrations (Lalamove). */
export function DemLalamoveWebhookPanel({ compact = false }: { compact?: boolean }) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSandbox, setWebhookSandbox] = useState<boolean | null>(null);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const webhook = await fetchLalamoveWebhookInfo().catch(() => null);
      if (webhook?.suggestedUrl) {
        setWebhookUrl(webhook.suggestedUrl);
        setWebhookSandbox(webhook.sandbox);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhook info.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRegisterWebhook = async () => {
    setWebhookBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await registerLalamoveWebhook(webhookUrl || undefined);
      setWebhookUrl(result.url);
      setWebhookSandbox(result.sandbox);
      setNotice(
        `Lalamove webhook registered${result.sandbox ? ' (sandbox)' : ''}: ${result.url}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register webhook.');
    } finally {
      setWebhookBusy(false);
    }
  };

  return (
    <div className={`dem-delivery-settings__webhook${compact ? ' dem-delivery-settings__webhook--compact' : ''}`}>
      {!compact ? <h3 className="settings-panel__subtitle">Lalamove webhook</h3> : null}
      <p className="settings-panel__lead">
        Lalamove pushes status / fee / driver updates to this URL so deliveries stay current without
        waiting for the page poll. Use Webhook version 3 in Partner Portal.
        {webhookSandbox != null ? (
          <> Current API mode: {webhookSandbox ? 'Sandbox' : 'Production'}.</>
        ) : null}
      </p>
      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {loading ? <p className="settings-empty">Loading webhook…</p> : null}
      <label className="field">
        <span className="field-label">Webhook URL</span>
        <input
          className="field-input"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://….supabase.co/functions/v1/lalamove-webhook"
          disabled={loading || webhookBusy}
        />
      </label>
      <div className="dem-delivery-settings__actions">
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={webhookBusy || !webhookUrl.trim()}
          onClick={() => void handleRegisterWebhook()}
        >
          {webhookBusy ? 'Registering…' : 'Register with Lalamove'}
        </button>
      </div>
    </div>
  );
}
